import {
  acceptInvitationSchema,
  authUserResponseSchema,
  createFirstAdminSchema,
  invitationPreviewSchema,
  loginRequestSchema,
  meResponseSchema,
  passwordResetCompleteSchema,
  passwordResetPreviewResponseSchema,
  passwordResetPreviewSchema,
  passwordResetRequestSchema,
  readinessResponseSchema,
  sessionsResponseSchema,
  setupStatusResponseSchema,
  type AcceptInvitationInput,
  type CreateFirstAdminInput,
  type LoginRequest,
  type PasswordResetComplete,
  type PasswordResetRequest,
} from '@bemmoly/shared';
import type { Http } from '../http.ts';
import { enc, validated } from './validate.ts';

/** First-run wizard. Anonymous by design. */
export function setupEndpoints(http: Http) {
  return {
    status: async () => http.request('/api/v1/setup/status', setupStatusResponseSchema),
    createAdmin: async (body: CreateFirstAdminInput) =>
      http.request('/api/v1/setup/admin', authUserResponseSchema, {
        method: 'POST',
        body: validated(createFirstAdminSchema, body),
      }),
    /** Anonymous readiness probe; the wizard's first health row before an admin exists. */
    readiness: async () => http.request('/readyz', readinessResponseSchema),
  };
}

/** Sign-in flows; anonymous by design and rate limited per IP on the server. */
export function authEndpoints(http: Http) {
  return {
    /** `signal` lets the sign-in page give up on a server that never answers. */
    login: async (body: LoginRequest, options: { signal?: AbortSignal } = {}) =>
      http.request('/api/v1/auth/login', authUserResponseSchema, {
        method: 'POST',
        body: validated(loginRequestSchema, body),
        ...(options.signal ? { signal: options.signal } : {}),
      }),
    logout: async (everywhere = false) =>
      http.send('/api/v1/auth/logout', { method: 'POST', body: everywhere ? { everywhere } : {} }),
    requestPasswordReset: async (body: PasswordResetRequest) =>
      http.send('/api/v1/auth/password-reset', {
        method: 'POST',
        body: validated(passwordResetRequestSchema, body),
      }),
    /** Whose account a reset link is for; the link stays usable. */
    previewPasswordReset: async (token: string) =>
      http.request('/api/v1/auth/password-reset/preview', passwordResetPreviewResponseSchema, {
        method: 'POST',
        body: validated(passwordResetPreviewSchema, { token }),
      }),
    /** Saves the password and signs in on this browser. */
    completePasswordReset: async (body: PasswordResetComplete) =>
      http.request('/api/v1/auth/password-reset/complete', authUserResponseSchema, {
        method: 'POST',
        body: validated(passwordResetCompleteSchema, body),
      }),
    invitation: async (token: string) =>
      http.request(`/api/v1/auth/invitations/${enc(token)}`, invitationPreviewSchema),
    acceptInvitation: async (token: string, body: AcceptInvitationInput) =>
      http.request(`/api/v1/auth/invitations/${enc(token)}/accept`, authUserResponseSchema, {
        method: 'POST',
        body: validated(acceptInvitationSchema, body),
      }),
    me: async () => http.request('/api/v1/me', meResponseSchema),
    sessions: async () => http.request('/api/v1/sessions', sessionsResponseSchema),
    revokeSession: async (id: string) =>
      http.send(`/api/v1/sessions/${enc(id)}`, { method: 'DELETE' }),
  };
}
